import * as Clipboard from 'expo-clipboard';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SquadViewModal } from '@/components/fantasy/SquadViewModal';
import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import {
  useCreateMiniLeague,
  useJoinMiniLeague,
  useMyFantasyTeam,
  useMyMiniLeagues,
  useOverallLeaderboard,
} from '@/features/fantasy/hooks';
import { useThemePalette } from '@/config/theme';
import { formatNumber, latinNumerals } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { LeaderboardEntry } from '@/lib/supabase/types';
import { errorKey } from '@/lib/utils/errors';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53']; // gold · silver · bronze

/** Rank pill - a coloured medal for the top three, a plain number otherwise. */
function RankBadge({ rank, label }: { rank: number; label: string }) {
  const medal = rank <= 3 ? PODIUM[rank - 1] : null;
  if (medal) {
    return (
      <View
        className="h-7 w-7 items-center justify-center rounded-full"
        style={{ backgroundColor: medal }}
      >
        <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 12 }}>
          {label}
        </AppText>
      </View>
    );
  }
  return (
    <AppText variant="caption" weight="bold" tabular className="w-7 text-center text-muted">
      {label}
    </AppText>
  );
}

function LeaderboardRows({
  entries,
  myTeamId,
  onSelect,
}: {
  entries: LeaderboardEntry[];
  myTeamId: string | null;
  onSelect: (entry: LeaderboardEntry) => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const nums = latinNumerals(locale); // leaderboards always read in Latin digits

  return (
    <Card className="mx-4 overflow-hidden">
      {entries.map((entry, index) => {
        const isMine = entry.fantasy_team_id === myTeamId;
        const top = entry.rank === 1;
        return (
          <Pressable
            key={entry.fantasy_team_id}
            accessibilityRole="button"
            accessibilityLabel={t('fantasy.leaderboards.viewSquad', { team: entry.team_name })}
            onPress={() => onSelect(entry)}
            className={`flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/60 ${
              index > 0 ? 'border-t border-line' : ''
            } ${isMine ? 'bg-volt/10' : ''}`}
            style={top ? { borderLeftWidth: 3, borderLeftColor: palette.captain } : undefined}
          >
            <RankBadge rank={entry.rank} label={formatNumber(entry.rank, nums)} />
            <PlayerAvatar uri={entry.avatar_url} name={entry.display_name} size={34} />
            <View className="flex-1">
              <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                {entry.team_name}
                {isMine ? `  ·  ${t('fantasy.leaderboards.you')}` : ''}
              </AppText>
              <AppText variant="micro" className="text-muted" numberOfLines={1}>
                {entry.display_name}
                {entry.captain_name
                  ? `  ·  ${t('fantasy.leaderboards.captainShort', { name: entry.captain_name })}`
                  : ''}
              </AppText>
            </View>
            <AppText variant="body" weight="bold" tabular className="text-volt">
              {formatNumber(entry.total_points, nums)}
            </AppText>
          </Pressable>
        );
      })}
    </Card>
  );
}

export default function LeaderboardsScreen() {
  const { seasonId } = useLocalSearchParams<{ seasonId: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const [tab, setTab] = useState<'overall' | 'leagues'>('overall');
  const [leagueName, setLeagueName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<LeaderboardEntry | null>(null);

  const overallQuery = useOverallLeaderboard(seasonId);
  const myTeamQuery = useMyFantasyTeam(seasonId);
  const leaguesQuery = useMyMiniLeagues();
  const createLeague = useCreateMiniLeague();
  const joinLeague = useJoinMiniLeague();

  const seasonLeagues = (leaguesQuery.data ?? []).filter(
    (membership) => membership.mini_league?.fantasy_season_id === seasonId,
  );

  const onCreate = () => {
    setFormMessage(null);
    setCreatedCode(null);
    if (!seasonId) return;
    createLeague.mutate(
      { fantasySeasonId: seasonId, name: leagueName.trim() },
      {
        onSuccess: (league) => {
          setCreatedCode(league.invite_code);
          setLeagueName('');
        },
        onError: (error) => setFormMessage(t(errorKey(error))),
      },
    );
  };

  const onJoin = () => {
    setFormMessage(null);
    joinLeague.mutate(inviteCode.trim().toUpperCase(), {
      onSuccess: () => setInviteCode(''),
      onError: (error) => setFormMessage(t(errorKey(error))),
    });
  };

  return (
    <Screen>
      <BackHeader title={t('fantasy.leaderboards.title')} />
      <View className="pb-4">
        <Segmented
          options={[
            { value: 'overall', label: t('fantasy.leaderboards.overall') },
            { value: 'leagues', label: t('fantasy.leaderboards.miniLeagues') },
          ]}
          value={tab}
          onChange={setTab}
        />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 48 }}>
        {tab === 'overall' ? (
          overallQuery.isPending ? (
            <View className="gap-3 px-4">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </View>
          ) : overallQuery.isError ? (
            <ErrorState error={overallQuery.error} onRetry={() => overallQuery.refetch()} />
          ) : (overallQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="podium-outline"
              title={t('fantasy.leaderboards.emptyOverallTitle')}
              body={t('fantasy.leaderboards.emptyOverallBody')}
            />
          ) : (
            <LeaderboardRows
              entries={overallQuery.data ?? []}
              myTeamId={myTeamQuery.data?.id ?? null}
              onSelect={setSelectedEntry}
            />
          )
        ) : (
          <View className="gap-4">
            <Card className="mx-4 gap-3 px-4 py-4">
              <AppText variant="heading">{t('fantasy.leaderboards.createLeague')}</AppText>
              <TextField
                placeholder={t('fantasy.leaderboards.leagueNamePlaceholder')}
                value={leagueName}
                onChangeText={setLeagueName}
                maxLength={30}
              />
              <Button
                label={t('fantasy.leaderboards.createLeague')}
                compact
                loading={createLeague.isPending}
                disabled={leagueName.trim().length < 3}
                onPress={onCreate}
              />
              {createdCode ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    Clipboard.setStringAsync(createdCode).catch(() => {
                      // Clipboard unavailable - the code stays visible to copy manually.
                    });
                  }}
                  className="rounded-xl bg-volt/10 px-3 py-2.5"
                >
                  <AppText variant="caption" weight="bold" tabular className="text-volt">
                    {t('fantasy.leaderboards.inviteCodeShare', { code: createdCode })}
                  </AppText>
                </Pressable>
              ) : null}
            </Card>

            <Card className="mx-4 gap-3 px-4 py-4">
              <AppText variant="heading">{t('fantasy.leaderboards.joinLeague')}</AppText>
              <TextField
                placeholder={t('fantasy.leaderboards.inviteCodePlaceholder')}
                value={inviteCode}
                onChangeText={setInviteCode}
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={12}
              />
              <Button
                label={t('fantasy.leaderboards.joinLeague')}
                compact
                loading={joinLeague.isPending}
                disabled={inviteCode.trim().length !== 12}
                onPress={onJoin}
              />
            </Card>

            {formMessage ? (
              <AppText variant="caption" className="px-6 text-loss">
                {formMessage}
              </AppText>
            ) : null}

            {leaguesQuery.isPending ? (
              <View className="gap-3 px-4">
                <Skeleton className="h-14 w-full" />
              </View>
            ) : leaguesQuery.isError ? (
              <ErrorState error={leaguesQuery.error} onRetry={() => leaguesQuery.refetch()} />
            ) : seasonLeagues.length === 0 ? (
              <EmptyState
                icon="people-outline"
                title={t('fantasy.leaderboards.noLeaguesTitle')}
                body={t('fantasy.leaderboards.noLeaguesBody')}
              />
            ) : (
              <Card className="mx-4 overflow-hidden">
                {seasonLeagues.map((membership, index) =>
                  membership.mini_league ? (
                    <Pressable
                      key={membership.mini_league.id}
                      accessibilityRole="button"
                      onPress={() => router.push(`/fantasy/league/${membership.mini_league?.id}`)}
                      className={`flex-row items-center justify-between px-4 py-3.5 active:bg-surface-2/60 ${
                        index > 0 ? 'border-t border-line' : ''
                      }`}
                    >
                      <AppText variant="caption" weight="semibold" className="text-ink">
                        {membership.mini_league.name}
                      </AppText>
                      <AppText variant="micro" tabular className="text-muted">
                        {membership.mini_league.invite_code}
                      </AppText>
                    </Pressable>
                  ) : null,
                )}
              </Card>
            )}
          </View>
        )}
      </ScrollView>

      <SquadViewModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </Screen>
  );
}
